const STORAGE_KEY = 'cdw-saved-project-v2';
const LEGACY_STORAGE_KEY = 'cdw-saved-project-v1';
const MAX_ITEMS = 80;

const emptyProject = () => ({
  items: [],
  roomName: '',
  notes: '',
});

const normalizeItem = (item) => ({
  ...item,
  quantity: Math.max(1, Math.min(99, Number.parseInt(item.quantity, 10) || 1)),
  unitPrice: Number.parseInt(item.unitPrice, 10) || 0,
});

const readProject = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
    if (stored && Array.isArray(stored.items)) {
      return {
        ...emptyProject(),
        ...stored,
        items: stored.items.filter((item) => item && item.id && item.title).slice(0, MAX_ITEMS).map(normalizeItem),
      };
    }

    const legacy = JSON.parse(window.localStorage.getItem(LEGACY_STORAGE_KEY) || '[]');
    if (Array.isArray(legacy) && legacy.length) {
      const project = { ...emptyProject(), items: legacy.slice(0, MAX_ITEMS).map(normalizeItem) };
      writeProject(project);
      return project;
    }
  } catch (_error) {
    // Continue with an empty schedule if storage is unavailable or malformed.
  }
  return emptyProject();
};

const writeProject = (project) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...emptyProject(),
      ...project,
      items: project.items.slice(0, MAX_ITEMS),
    }));
  } catch (_error) {
    // The storefront remains usable when storage is unavailable.
  }
};

const roots = () => [...document.querySelectorAll('[data-cdw-project-root]')];

const itemFromButton = (button) => ({
  id: button.dataset.variantId || button.dataset.productId,
  productId: button.dataset.productId || '',
  variantId: button.dataset.variantId || '',
  title: button.dataset.productTitle || 'Cabinet product',
  sku: button.dataset.productSku || '',
  finish: button.dataset.productFinish || '',
  price: button.dataset.productPrice || '',
  unitPrice: Number.parseInt(button.dataset.productUnitPrice, 10) || 0,
  url: button.dataset.productUrl || '',
  image: button.dataset.productImage || '',
  quantity: 1,
});

const announce = (message) => {
  roots().forEach((root) => {
    const status = root.querySelector('[data-cdw-project-status]');
    if (status) status.textContent = message;
  });
};

const escapeHtml = (value) => String(value)
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;');

const money = (cents) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
}).format((Number(cents) || 0) / 100);

const projectSubtotal = (items) => items.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);

const scheduleLines = (items) => items.map((item) => {
  const details = [item.sku, item.finish].filter(Boolean).join(' · ');
  return `${item.quantity} × ${item.title}${details ? ` — ${details}` : ''}`;
});

const quoteUrl = (project) => {
  const params = new URLSearchParams();
  params.set('reason', 'Project quote and availability review');
  params.set('skus', project.items.map((item) => `${item.quantity}x ${item.sku}`).filter(Boolean).join(', '));
  params.set('products', scheduleLines(project.items).join('\n'));
  params.set('source', 'Cabinet schedule');
  if (project.roomName) params.set('room', project.roomName);
  if (project.notes) params.set('notes', project.notes);
  params.set('subtotal', money(projectSubtotal(project.items)));
  return `/pages/contact?${params.toString()}`;
};

const itemMarkup = (item) => {
  const image = item.image
    ? `<img src="${escapeHtml(item.image)}" alt="" width="72" height="72" loading="lazy">`
    : '<span class="cdw-project__image-placeholder" aria-hidden="true"></span>';
  const finish = item.finish ? `<span>${escapeHtml(item.finish)}</span>` : '';
  const sku = item.sku ? `<span>SKU ${escapeHtml(item.sku)}</span>` : '';
  const price = item.unitPrice ? `<strong>${money(item.unitPrice * item.quantity)}</strong>` : `<strong>${escapeHtml(item.price || 'Price review')}</strong>`;
  return `<li class="cdw-project__item" data-cdw-project-row="${escapeHtml(item.id)}">
    <a href="${escapeHtml(item.url || '#')}">${image}</a>
    <div class="cdw-project__item-copy">
      <a href="${escapeHtml(item.url || '#')}">${escapeHtml(item.title)}</a>
      <p>${sku}${finish}</p>
      <label class="cdw-project__quantity">
        <span>Qty</span>
        <input type="number" min="1" max="99" inputmode="numeric" value="${item.quantity}" data-cdw-project-quantity="${escapeHtml(item.id)}" aria-label="Quantity for ${escapeHtml(item.title)}">
      </label>
      ${price}
    </div>
    <button type="button" data-cdw-project-remove="${escapeHtml(item.id)}" aria-label="Remove ${escapeHtml(item.title)} from cabinet schedule">Remove</button>
  </li>`;
};

const setInputValue = (root, selector, value) => {
  const input = root.querySelector(selector);
  if (input && document.activeElement !== input) input.value = value || '';
};

const render = () => {
  const project = readProject();
  const savedIds = new Set(project.items.map((item) => String(item.id)));
  const totalUnits = project.items.reduce((sum, item) => sum + item.quantity, 0);

  document.querySelectorAll('[data-cdw-mobile-project-open]').forEach((button) => {
    button.toggleAttribute('data-has-items', project.items.length > 0);
  });
  document.querySelectorAll('[data-cdw-mobile-project-count]').forEach((count) => {
    count.textContent = String(totalUnits);
  });

  document.querySelectorAll('[data-cdw-project-save]').forEach((button) => {
    const id = button.dataset.variantId || button.dataset.productId;
    const saved = savedIds.has(String(id));
    button.setAttribute('aria-pressed', String(saved));
    const label = button.querySelector('[data-cdw-save-label]');
    if (label) label.textContent = saved ? 'Saved to schedule' : button.dataset.defaultLabel || 'Save to project';
  });

  roots().forEach((root) => {
    const launcher = root.querySelector('[data-cdw-project-open]');
    const count = root.querySelector('[data-cdw-project-count]');
    const list = root.querySelector('[data-cdw-project-items]');
    const empty = root.querySelector('[data-cdw-project-empty]');
    const clear = root.querySelector('[data-cdw-project-clear]');
    const quote = root.querySelector('[data-cdw-project-quote]');
    const subtotal = root.querySelector('[data-cdw-project-subtotal]');
    const units = root.querySelector('[data-cdw-project-units]');

    if (launcher) launcher.hidden = project.items.length === 0;
    if (count) count.textContent = String(totalUnits);
    if (list) list.innerHTML = project.items.map(itemMarkup).join('');
    if (empty) empty.hidden = project.items.length !== 0;
    if (clear) clear.hidden = project.items.length === 0;
    if (subtotal) subtotal.textContent = money(projectSubtotal(project.items));
    if (units) units.textContent = `${totalUnits} cabinet unit${totalUnits === 1 ? '' : 's'} · ${project.items.length} unique SKU${project.items.length === 1 ? '' : 's'}`;
    setInputValue(root, '[data-cdw-project-room]', project.roomName);
    setInputValue(root, '[data-cdw-project-notes]', project.notes);
    if (quote) {
      quote.href = project.items.length ? quoteUrl(project) : '/pages/contact?reason=Project%20quote%20and%20availability%20review';
      quote.textContent = project.items.length ? 'Request one project quote' : 'Request project review';
      quote.setAttribute('aria-disabled', String(project.items.length === 0));
    }
  });
};

const updateProjectField = (field, value) => {
  const project = readProject();
  project[field] = value;
  writeProject(project);
};

const syncMobileDockRoute = () => {
  const dock = document.querySelector('[data-cdw-mobile-dock]');
  if (!dock) return;

  const projectDialog = document.querySelector('[data-cdw-project-dialog]');
  const activeTab = projectDialog?.open
    ? 'project'
    : window.location.hash === '#finish-collections'
      ? 'finishes'
      : dock.dataset.cdwActiveTab;

  dock.querySelectorAll('[data-cdw-app-tab]').forEach((tab) => {
    if (tab.dataset.cdwAppTab === activeTab) tab.setAttribute('aria-current', 'page');
    else tab.removeAttribute('aria-current');
    if (tab.matches('[data-cdw-mobile-project-open]')) {
      tab.setAttribute('aria-expanded', String(Boolean(projectDialog?.open)));
    }
  });
};

const exportSchedule = () => {
  const project = readProject();
  if (!project.items.length) return;
  const csvRows = [
    ['Room', 'Quantity', 'SKU', 'Finish', 'Product', 'Unit price', 'Line subtotal', 'URL'],
    ...project.items.map((item) => [
      project.roomName,
      item.quantity,
      item.sku,
      item.finish,
      item.title,
      money(item.unitPrice),
      money(item.unitPrice * item.quantity),
      new URL(item.url || '/', window.location.origin).toString(),
    ]),
  ];
  const csv = csvRows.map((row) => row.map((value) => `"${String(value || '').replaceAll('"', '""')}"`).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `cdw-cabinet-schedule-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

const prefillContact = () => {
  const container = document.querySelector('[data-cdw-contact-form]');
  if (!container) return false;
  const embed = container.querySelector('shopify-forms-embed');
  const form = embed?.shadowRoot?.querySelector('form') || container.querySelector('form');
  if (!form) return false;

  const params = new URLSearchParams(window.location.search);
  const reason = params.get('reason');
  const skus = params.get('skus') || params.get('sku');
  const products = params.get('products') || params.get('product');
  const source = params.get('source');
  const room = params.get('room');
  const notes = params.get('notes');
  const subtotal = params.get('subtotal');
  const reasonField = form.querySelector('[data-cdw-contact-reason]');
  const skuField = form.querySelector('[data-cdw-contact-skus], [id="custom#cabinet_skus_if_known"]');
  const messageField = form.querySelector('[data-cdw-contact-message], textarea[name="custom#project_details"]');
  const sourceField = form.querySelector('[data-cdw-contact-source]');

  const setFormValue = (field, value) => {
    if (!field || !value || field.value) return;
    const prototype = field instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;
    if (setter) setter.call(field, value);
    else field.value = value;
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));
  };

  if (reason && reasonField) {
    const matchingOption = [...reasonField.options].find((option) => option.textContent.trim() === reason);
    if (matchingOption) reasonField.value = matchingOption.value;
  }
  if (skus) setFormValue(skuField, skus);
  if ((products || reason || source) && messageField && !messageField.value) {
    const projectDetails = [
      reason ? `Request type: ${reason}` : null,
      products ? `Please review availability and project pricing for:\n${products}` : null,
      room ? `Room / project: ${room}` : null,
      subtotal ? `Working product subtotal: ${subtotal}` : null,
      notes ? `Room notes: ${notes}` : null,
      source ? `Request source: ${source}` : null,
      'Additional project details:',
    ].filter(Boolean);
    setFormValue(messageField, projectDetails.join('\n\n'));
  }
  if (source && sourceField) sourceField.value = source;
  return Boolean(skuField || messageField || reasonField);
};

document.addEventListener('input', (event) => {
  const quantity = event.target.closest('[data-cdw-project-quantity]');
  if (quantity instanceof HTMLInputElement) {
    const project = readProject();
    const item = project.items.find((row) => String(row.id) === String(quantity.dataset.cdwProjectQuantity));
    if (item) item.quantity = Math.max(1, Math.min(99, Number.parseInt(quantity.value, 10) || 1));
    writeProject(project);
    render();
    return;
  }

  const room = event.target.closest('[data-cdw-project-room]');
  if (room instanceof HTMLInputElement) updateProjectField('roomName', room.value);
  const notes = event.target.closest('[data-cdw-project-notes]');
  if (notes instanceof HTMLTextAreaElement) updateProjectField('notes', notes.value);
});

document.addEventListener('click', (event) => {
  const saveButton = event.target.closest('[data-cdw-project-save]');
  if (saveButton) {
    event.preventDefault();
    event.stopPropagation();
    const item = itemFromButton(saveButton);
    const project = readProject();
    const index = project.items.findIndex((saved) => String(saved.id) === String(item.id));
    if (index >= 0) {
      project.items.splice(index, 1);
      announce(`${item.title} removed from your cabinet schedule.`);
    } else {
      project.items.unshift(item);
      announce(`${item.title} saved to your cabinet schedule.`);
    }
    writeProject(project);
    render();
    return;
  }

  const openButton = event.target.closest('[data-cdw-project-open], [data-cdw-mobile-project-open]');
  if (openButton) {
    const dialog = openButton.closest('[data-cdw-project-root]')?.querySelector('[data-cdw-project-dialog]')
      || document.querySelector('[data-cdw-project-dialog]');
    if (dialog && !dialog.open) {
      try { dialog.showModal(); } catch (_error) { dialog.setAttribute('open', ''); }
      if (!dialog.open) dialog.setAttribute('open', '');
    }
    syncMobileDockRoute();
    return;
  }

  const closeButton = event.target.closest('[data-cdw-project-close]');
  if (closeButton) {
    const dialog = closeButton.closest('dialog');
    if (dialog) {
      try { dialog.close(); } catch (_error) { dialog.removeAttribute('open'); }
      if (dialog.open) dialog.removeAttribute('open');
    }
    syncMobileDockRoute();
    return;
  }

  const removeButton = event.target.closest('[data-cdw-project-remove]');
  if (removeButton) {
    const project = readProject();
    project.items = project.items.filter((item) => String(item.id) !== String(removeButton.dataset.cdwProjectRemove));
    writeProject(project);
    render();
    announce('Item removed from your cabinet schedule.');
    return;
  }

  if (event.target.closest('[data-cdw-project-export]')) {
    exportSchedule();
    return;
  }

  if (event.target.closest('[data-cdw-project-clear]')) {
    const project = readProject();
    project.items = [];
    writeProject(project);
    render();
    announce('Cabinet schedule cleared.');
  }
});

document.addEventListener('shopify:section:load', render);
window.addEventListener('hashchange', syncMobileDockRoute);
document.addEventListener('cdw:catalog:updated', render);
document.addEventListener('shopify:collection:update', (event) => {
  event.detail?.promise?.then(() => window.requestAnimationFrame(render));
});
document.addEventListener('shopify:product:select', (event) => {
  event.detail?.promise?.then(() => window.requestAnimationFrame(render));
});
document.addEventListener('DOMContentLoaded', () => {
  render();
  if (!prefillContact()) {
    const prefillTimer = window.setInterval(() => {
      if (prefillContact()) window.clearInterval(prefillTimer);
    }, 250);
    window.setTimeout(() => window.clearInterval(prefillTimer), 12000);
  }
  syncMobileDockRoute();
  document.querySelectorAll('[data-cdw-project-dialog]').forEach((dialog) => {
    dialog.addEventListener('close', syncMobileDockRoute);
  });
});

/**
 * Presentation-only adapter for Shopify Forms 1112127. Load as a deferred script.
 * Shopify Forms remains the source of truth for consent, validation and uploads.
 * No input values, checked states, required flags, submissions or network calls
 * are changed. Unknown DOM shapes fail open to Shopify's original disclaimer.
 * This relies on observed Shopify shadow DOM, not a documented extension API.
 */
(() => {
  'use strict';

  const INSTANCE = Symbol.for('cdw.singleConsentEnhancer.v1');
  if (window[INSTANCE]) {
    window[INSTANCE].refresh();
    return;
  }

  const FORM_ID = '1112127';
  const FIELD_NAME = 'custom#sms_consent_optional';
  const OLD_FIELD_NAME = 'custom#text_messages_from_cabinets_discount_warehouse_optional';
  const DISCLOSURE_ID = 'cdw-sms-disclosure-1112127';
  const ROOT_SELECTOR = '[data-forms-id="forms-root-1112127"]';
  const SHELL_SELECTOR = '[data-cdw-contact-form]';
  const shells = document.querySelectorAll(SHELL_SELECTOR);
  if (shells.length !== 1) return;
  const shell = shells[0];
  const DISCLOSURE_ATTRIBUTE = 'data-cdw-sms-disclosure';
  const HIDDEN_ATTRIBUTE = 'data-cdw-sms-native-disclaimer';
  const FIELDSET_ATTRIBUTE = 'data-cdw-sms-fieldset';
  const ALLOWED_TAGS = new Set(['P', 'BR', 'A', 'SPAN', 'STRONG', 'B', 'EM', 'I', 'UL', 'OL', 'LI']);
  const STORE_ORIGIN = 'https://cabinetsdiscountwarehouse.com';
  const records = new Map();
  let stopped = false;
  let scheduled = false;
  let bootTimer = null;
  let bootAttempts = 0;

  const css = `
    [${HIDDEN_ATTRIBUTE}="${FORM_ID}"] { display: none !important; }
    [${FIELDSET_ATTRIBUTE}="${FORM_ID}"] label { align-items: flex-start !important; }
    [${DISCLOSURE_ATTRIBUTE}="${FORM_ID}"] {
      margin: 8px 0 0;
      color: inherit;
      font: inherit;
      font-size: 13px;
      line-height: 1.5;
      text-align: left;
      overflow-wrap: anywhere;
    }
    [${DISCLOSURE_ATTRIBUTE}="${FORM_ID}"] p { margin: 0 0 8px; }
    [${DISCLOSURE_ATTRIBUTE}="${FORM_ID}"] p:last-child { margin-bottom: 0; }
    [${DISCLOSURE_ATTRIBUTE}="${FORM_ID}"] a { text-decoration: underline; text-underline-offset: 2px; }
  `;

  // Copy only inert text/formatting and same-store HTTPS links. No source IDs,
  // inline handlers, styles, scripts, controls or ARIA references are copied.
  const copyDisclosure = (source) => {
    const copy = document.createElement('div');
    copy.id = DISCLOSURE_ID;
    copy.setAttribute(DISCLOSURE_ATTRIBUTE, FORM_ID);
    let hasPrivacyLink = false;
    const append = (node, parent) => {
      if (node.nodeType === Node.TEXT_NODE) {
        parent.appendChild(document.createTextNode(node.textContent));
        return;
      }
      if (node.nodeType === Node.COMMENT_NODE) return;
      if (node.nodeType !== Node.ELEMENT_NODE || !ALLOWED_TAGS.has(node.tagName)) {
        throw new Error('Unexpected disclosure element');
      }
      const element = document.createElement(node.tagName.toLowerCase());
      if (node.tagName === 'A') {
        const href = node.getAttribute('href');
        if (!href) throw new Error('Missing disclosure link');
        const url = new URL(href, STORE_ORIGIN);
        if (url.origin !== STORE_ORIGIN || url.protocol !== 'https:' || url.username || url.password) {
          throw new Error('Unexpected disclosure link');
        }
        element.setAttribute('href', url.href);
        if (node.getAttribute('target') === '_blank') {
          element.setAttribute('target', '_blank');
          element.setAttribute('rel', 'noopener noreferrer');
        }
        if (node.hasAttribute('title')) element.setAttribute('title', node.getAttribute('title'));
        if (url.pathname.replace(/\/$/, '') === '/policies/privacy-policy') hasPrivacyLink = true;
      }
      for (const child of node.childNodes) append(child, element);
      parent.appendChild(element);
    };
    for (const child of source.childNodes) append(child, copy);
    if (!copy.textContent.trim() || !hasPrivacyLink) throw new Error('Incomplete disclosure');
    return copy;
  };

  const removeDescription = (checkbox) => {
    if (!checkbox) return;
    const tokens = (checkbox.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
    const remaining = tokens.filter((token) => token !== DISCLOSURE_ID);
    if (remaining.length) checkbox.setAttribute('aria-describedby', remaining.join(' '));
    else checkbox.removeAttribute('aria-describedby');
  };

  const release = (record) => {
    record.copy?.remove();
    if (record.source?.getAttribute(HIDDEN_ATTRIBUTE) === FORM_ID) record.source.removeAttribute(HIDDEN_ATTRIBUTE);
    if (record.fieldset?.getAttribute(FIELDSET_ATTRIBUTE) === FORM_ID) record.fieldset.removeAttribute(FIELDSET_ATTRIBUTE);
    removeDescription(record.checkbox);
    record.copy = record.source = record.fieldset = record.checkbox = null;
  };

  const observe = (record) => record.observer.observe(record.shadow, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['class', 'href', 'target', 'title', 'type', 'name', 'id', 'required', 'checked', 'aria-describedby'],
  });

  const sync = (record) => {
    record.observer.disconnect();
    try {
      const forms = record.shadow.querySelectorAll('form');
      if (forms.length !== 1) throw new Error('Ambiguous form');
      const form = forms[0];
      const checkboxes = form.querySelectorAll('input[type="checkbox"]');
      const checkbox = checkboxes[0];
      if (checkboxes.length !== 1 || checkbox.name !== FIELD_NAME || !checkbox.id.startsWith(`${FIELD_NAME}-`)
        || checkbox.required || checkbox.defaultChecked || form.querySelector(`[name="${OLD_FIELD_NAME}"]`)) {
        throw new Error('Unexpected consent field');
      }
      const fieldset = checkbox.closest('fieldset');
      const label = checkbox.closest('label');
      const submitButtons = form.querySelectorAll('button[type="submit"]');
      const submit = submitButtons[0];
      if (!fieldset || fieldset.parentElement !== form || !label || !fieldset.contains(label)
        || submitButtons.length !== 1 || submit.parentElement !== form
        || form.lastElementChild !== submit || submit.previousElementSibling !== fieldset) {
        throw new Error('Unexpected consent placement');
      }
      const sources = [...form.parentElement.children].filter((element) => element.tagName === 'DIV'
        && [...element.classList].some((token) => token.startsWith('_formDisclaimer_')));
      const source = sources[0];
      if (sources.length !== 1 || source.previousElementSibling !== form) throw new Error('Missing native disclaimer');
      const candidate = copyDisclosure(source);
      const existingId = record.shadow.getElementById(DISCLOSURE_ID);
      if (existingId && existingId !== record.copy) throw new Error('Disclosure ID already in use');

      if (record.checkbox !== checkbox || record.fieldset !== fieldset || record.source !== source) release(record);
      record.checkbox = checkbox;
      record.fieldset = fieldset;
      record.source = source;
      if (!record.copy || record.copy.parentElement !== fieldset || record.copy.innerHTML !== candidate.innerHTML) {
        record.copy?.remove();
        record.copy = candidate;
        fieldset.appendChild(candidate);
      }
      if (!record.style.isConnected) record.shadow.appendChild(record.style);
      if (!record.copy.isConnected || record.copy.parentElement !== fieldset) throw new Error('Disclosure not attached');

      const tokens = new Set((checkbox.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
      tokens.add(DISCLOSURE_ID);
      checkbox.setAttribute('aria-describedby', [...tokens].join(' '));
      fieldset.setAttribute(FIELDSET_ATTRIBUTE, FORM_ID);
      // Hide the native presentation only after the complete linked copy exists.
      source.setAttribute(HIDDEN_ATTRIBUTE, FORM_ID);
    } catch (_error) {
      release(record);
    } finally {
      if (!stopped && record.embed.isConnected) observe(record);
    }
  };

  const destroy = (record) => {
    record.observer.disconnect();
    release(record);
    record.style.remove();
    records.delete(record.embed);
  };

  const refresh = () => {
    if (stopped) return;
    const matches = shell.isConnected && shell.matches(SHELL_SELECTOR) ? [...shell.querySelectorAll('shopify-forms-embed')]
      .filter((embed) => embed.parentElement?.matches(ROOT_SELECTOR)) : [];
    // A duplicate embed is an unexpected integration state; enhance neither.
    const embed = matches.length === 1 ? matches[0] : null;
    for (const record of [...records.values()]) {
      if (record.embed !== embed || !record.embed.isConnected || record.shadow !== embed?.shadowRoot) destroy(record);
    }
    if (!embed?.shadowRoot) return;
    let record = records.get(embed);
    if (!record) {
      const style = document.createElement('style');
      style.setAttribute('data-cdw-sms-presentation', FORM_ID);
      style.textContent = css;
      record = { embed, shadow: embed.shadowRoot, style, checkbox: null, fieldset: null, source: null, copy: null };
      record.observer = new MutationObserver(() => sync(record));
      records.set(embed, record);
    }
    sync(record);
  };

  const scheduleRefresh = () => {
    if (scheduled || stopped) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      refresh();
    });
  };
  const shellObserver = new MutationObserver(scheduleRefresh);
  shellObserver.observe(shell, {
    childList: true, subtree: true, attributes: true, attributeFilter: ['data-forms-id', 'data-cdw-contact-form'],
  });
  const disconnect = () => {
    stopped = true;
    shellObserver.disconnect();
    window.clearInterval(bootTimer);
    for (const record of [...records.values()]) destroy(record);
    if (window[INSTANCE] === controller) delete window[INSTANCE];
  };
  const controller = { refresh, disconnect };
  window[INSTANCE] = controller;
  refresh();
  // attachShadow itself does not mutate light DOM. Poll only during bootstrap,
  // and stop as soon as a shadow observer is installed or after 12 seconds.
  if (!records.size) {
    bootTimer = window.setInterval(() => {
      refresh();
      bootAttempts += 1;
      if (stopped || records.size || bootAttempts >= 48) window.clearInterval(bootTimer);
    }, 250);
  }
  window.customElements?.whenDefined('shopify-forms-embed').then(scheduleRefresh);
})();
